/**
 * 📜 SIDE QUESTS - Optional parallel quest chains
 *
 * Side quests unlock based on main quest progress and offer
 * alternative rewards and storylines. They are organized into
 * "chains" - sequences of related quests.
 *
 * ADDING NEW SIDE QUESTS:
 * 1. Add a new chain to sideQuestChains
 * 2. Add quests with chain: 'chainId' and order: 1, 2, 3...
 * 3. Set unlockRequirements to control when quests appear
 */

// Helper to generate side quest ID
const sq = (chainId, num) => `side_${chainId}_${String(num).padStart(2, '0')}`;

// ═══════════════════════════════════════════════════════════════════════════
// SIDE QUEST CHAINS
// ═══════════════════════════════════════════════════════════════════════════

export const sideQuestChains = {
  explorer: {
    id: 'explorer',
    name: 'The Explorer\'s Path',
    icon: '🗺️',
    description: 'Master the art of exploration and discovery',
    totalQuests: 5,
  },
  merchant: {
    id: 'merchant',
    name: 'Merchant\'s Fortune',
    icon: '💰',
    description: 'Build a trading empire',
    totalQuests: 5,
  },
  scholar: {
    id: 'scholar',
    name: 'Path of Knowledge',
    icon: '📚',
    description: 'Pursue wisdom and learning',
    totalQuests: 5,
  },
  warrior: {
    id: 'warrior',
    name: 'Warrior\'s Trial',
    icon: '⚔️',
    description: 'Prove your combat prowess',
    totalQuests: 5,
  },
  builder: {
    id: 'builder',
    name: 'Master Builder',
    icon: '🏗️',
    description: 'Construct a grand civilization',
    totalQuests: 5,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// SIDE QUESTS
// ═══════════════════════════════════════════════════════════════════════════

export const sideQuests = {
  // ═══════════════════════════════════════════════════════════════════════════
  // EXPLORER CHAIN - Unlocks after quest_006 (Expanding Horizons)
  // ═══════════════════════════════════════════════════════════════════════════

  [sq('explorer', 1)]: {
    id: sq('explorer', 1),
    chain: 'explorer',
    order: 1,
    name: 'Cartographer\'s Ambition',
    description: 'Map out 25 tiles of the realm.',
    objectives: [{ type: 'tiles_explored', count: 25 }],
    rewards: { gold: 100, knowledge: 50 },
    unlockRequirements: { mainQuest: 'quest_006' },
  },

  [sq('explorer', 2)]: {
    id: sq('explorer', 2),
    chain: 'explorer',
    order: 2,
    name: 'Distant Shores',
    description: 'Explore 50 tiles and complete 10 expeditions.',
    objectives: [
      { type: 'tiles_explored', count: 50 },
      { type: 'expeditions_total', count: 10 },
    ],
    rewards: { gold: 300, knowledge: 150, gems: 25 },
    unlockRequirements: { sideQuest: sq('explorer', 1) },
  },

  [sq('explorer', 3)]: {
    id: sq('explorer', 3),
    chain: 'explorer',
    order: 3,
    name: 'Uncharted Territory',
    description: 'Explore 100 tiles.',
    objectives: [{ type: 'tiles_explored', count: 100 }],
    rewards: { gold: 750, magic: 200, artifacts: 50 },
    unlockRequirements: { sideQuest: sq('explorer', 2) },
  },

  [sq('explorer', 4)]: {
    id: sq('explorer', 4),
    chain: 'explorer',
    order: 4,
    name: 'Expedition Master',
    description: 'Complete 50 expeditions across all biomes.',
    objectives: [{ type: 'expeditions_total', count: 50 }],
    rewards: { gold: 2000, crystals: 500, artifacts: 150 },
    unlockRequirements: { sideQuest: sq('explorer', 3) },
  },

  [sq('explorer', 5)]: {
    id: sq('explorer', 5),
    chain: 'explorer',
    order: 5,
    name: 'World Cartographer',
    description: 'Explore 200 tiles and complete 100 expeditions.',
    objectives: [
      { type: 'tiles_explored', count: 200 },
      { type: 'expeditions_total', count: 100 },
    ],
    rewards: { gold: 10000, magic: 5000, artifacts: 500 },
    unlockRequirements: { sideQuest: sq('explorer', 4) },
    isChainFinale: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MERCHANT CHAIN - Unlocks after quest_041 (Market)
  // ═══════════════════════════════════════════════════════════════════════════

  [sq('merchant', 1)]: {
    id: sq('merchant', 1),
    chain: 'merchant',
    order: 1,
    name: 'First Profit',
    description: 'Accumulate 1,000 gold.',
    objectives: [{ type: 'resource', target: 'gold', count: 1000 }],
    rewards: { gold: 250, gems: 10 },
    unlockRequirements: { mainQuest: 'quest_041' },
  },

  [sq('merchant', 2)]: {
    id: sq('merchant', 2),
    chain: 'merchant',
    order: 2,
    name: 'Trade Routes',
    description: 'Build 2 Trading Posts and accumulate 5,000 gold.',
    objectives: [
      { type: 'building', target: 'tradingPost', count: 2 },
      { type: 'resource', target: 'gold', count: 5000 },
    ],
    rewards: { gold: 1000, gems: 50 },
    unlockRequirements: { sideQuest: sq('merchant', 1) },
  },

  [sq('merchant', 3)]: {
    id: sq('merchant', 3),
    chain: 'merchant',
    order: 3,
    name: 'Banking Empire',
    description: 'Build 2 Banks and accumulate 20,000 gold.',
    objectives: [
      { type: 'building', target: 'bank', count: 2 },
      { type: 'resource', target: 'gold', count: 20000 },
    ],
    rewards: { gold: 5000, gems: 150 },
    unlockRequirements: { sideQuest: sq('merchant', 2) },
  },

  [sq('merchant', 4)]: {
    id: sq('merchant', 4),
    chain: 'merchant',
    order: 4,
    name: 'Gem Collector',
    description: 'Accumulate 1,000 gems.',
    objectives: [{ type: 'resource', target: 'gems', count: 1000 }],
    rewards: { gold: 15000, gems: 500, crystals: 200 },
    unlockRequirements: { sideQuest: sq('merchant', 3) },
  },

  [sq('merchant', 5)]: {
    id: sq('merchant', 5),
    chain: 'merchant',
    order: 5,
    name: 'Merchant Prince',
    description: 'Accumulate 100,000 gold and 5,000 gems.',
    objectives: [
      { type: 'resource', target: 'gold', count: 100000 },
      { type: 'resource', target: 'gems', count: 5000 },
    ],
    rewards: { gold: 50000, gems: 2500, artifacts: 1000 },
    unlockRequirements: { sideQuest: sq('merchant', 4) },
    isChainFinale: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SCHOLAR CHAIN - Unlocks after quest_023 (First Library)
  // ═══════════════════════════════════════════════════════════════════════════

  [sq('scholar', 1)]: {
    id: sq('scholar', 1),
    chain: 'scholar',
    order: 1,
    name: 'Pursuit of Knowledge',
    description: 'Accumulate 500 knowledge.',
    objectives: [{ type: 'resource', target: 'knowledge', count: 500 }],
    rewards: { knowledge: 150, culture: 50 },
    unlockRequirements: { mainQuest: 'quest_023' },
  },

  [sq('scholar', 2)]: {
    id: sq('scholar', 2),
    chain: 'scholar',
    order: 2,
    name: 'Academic Foundation',
    description: 'Build 2 Libraries and research 10 technologies.',
    objectives: [
      { type: 'building', target: 'library', count: 2 },
      { type: 'techs_researched', count: 10 },
    ],
    rewards: { knowledge: 500, culture: 200 },
    unlockRequirements: { sideQuest: sq('scholar', 1) },
  },

  [sq('scholar', 3)]: {
    id: sq('scholar', 3),
    chain: 'scholar',
    order: 3,
    name: 'Higher Learning',
    description: 'Build an Academy and research 25 technologies.',
    objectives: [
      { type: 'building', target: 'academy', count: 1 },
      { type: 'techs_researched', count: 25 },
    ],
    rewards: { knowledge: 2000, science: 500, culture: 500 },
    unlockRequirements: { sideQuest: sq('scholar', 2) },
  },

  [sq('scholar', 4)]: {
    id: sq('scholar', 4),
    chain: 'scholar',
    order: 4,
    name: 'Scientific Revolution',
    description: 'Build a University and accumulate 10,000 science.',
    objectives: [
      { type: 'building', target: 'university', count: 1 },
      { type: 'resource', target: 'science', count: 10000 },
    ],
    rewards: { knowledge: 5000, science: 3000, magic: 1000 },
    unlockRequirements: { sideQuest: sq('scholar', 3) },
  },

  [sq('scholar', 5)]: {
    id: sq('scholar', 5),
    chain: 'scholar',
    order: 5,
    name: 'Grand Scholar',
    description: 'Research 50 technologies and accumulate 50,000 knowledge.',
    objectives: [
      { type: 'techs_researched', count: 50 },
      { type: 'resource', target: 'knowledge', count: 50000 },
    ],
    rewards: { knowledge: 25000, science: 15000, artifacts: 2000 },
    unlockRequirements: { sideQuest: sq('scholar', 4) },
    isChainFinale: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // WARRIOR CHAIN - Unlocks after first dungeon clear
  // ═══════════════════════════════════════════════════════════════════════════

  [sq('warrior', 1)]: {
    id: sq('warrior', 1),
    chain: 'warrior',
    order: 1,
    name: 'First Blood',
    description: 'Clear any dungeon once.',
    objectives: [{ type: 'dungeons_cleared', count: 1 }],
    rewards: { gold: 200, starEssence: 25 },
    unlockRequirements: { chapter: 1 },
  },

  [sq('warrior', 2)]: {
    id: sq('warrior', 2),
    chain: 'warrior',
    order: 2,
    name: 'Dungeon Delver',
    description: 'Clear 10 dungeons.',
    objectives: [{ type: 'dungeons_cleared', count: 10 }],
    rewards: { gold: 1000, starEssence: 100 },
    unlockRequirements: { sideQuest: sq('warrior', 1) },
  },

  [sq('warrior', 3)]: {
    id: sq('warrior', 3),
    chain: 'warrior',
    order: 3,
    name: 'Boss Slayer',
    description: 'Defeat 10 dungeon bosses.',
    objectives: [{ type: 'bosses_defeated', count: 10 }],
    rewards: { gold: 3000, starEssence: 300 },
    unlockRequirements: { sideQuest: sq('warrior', 2) },
  },

  [sq('warrior', 4)]: {
    id: sq('warrior', 4),
    chain: 'warrior',
    order: 4,
    name: 'Veteran Warrior',
    description: 'Clear 50 dungeons and defeat 25 bosses.',
    objectives: [
      { type: 'dungeons_cleared', count: 50 },
      { type: 'bosses_defeated', count: 25 },
    ],
    rewards: { gold: 10000, starEssence: 1000, magic: 2000 },
    unlockRequirements: { sideQuest: sq('warrior', 3) },
  },

  [sq('warrior', 5)]: {
    id: sq('warrior', 5),
    chain: 'warrior',
    order: 5,
    name: 'Legendary Champion',
    description: 'Clear 100 dungeons and reach character level 50.',
    objectives: [
      { type: 'dungeons_cleared', count: 100 },
      { type: 'character_level', count: 50 },
    ],
    rewards: { gold: 50000, starEssence: 5000, artifacts: 3000 },
    unlockRequirements: { sideQuest: sq('warrior', 4) },
    isChainFinale: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BUILDER CHAIN - Unlocks after quest_013 (Five Buildings)
  // ═══════════════════════════════════════════════════════════════════════════

  [sq('builder', 1)]: {
    id: sq('builder', 1),
    chain: 'builder',
    order: 1,
    name: 'Foundation Layer',
    description: 'Own 10 buildings.',
    objectives: [{ type: 'buildings_total', count: 10 }],
    rewards: { wood: 200, stone: 200, gold: 100 },
    unlockRequirements: { mainQuest: 'quest_013' },
  },

  [sq('builder', 2)]: {
    id: sq('builder', 2),
    chain: 'builder',
    order: 2,
    name: 'Town Planner',
    description: 'Own 25 buildings and build on 25 tiles.',
    objectives: [
      { type: 'buildings_total', count: 25 },
      { type: 'tiles_built', count: 25 },
    ],
    rewards: { wood: 500, stone: 500, iron: 200 },
    unlockRequirements: { sideQuest: sq('builder', 1) },
  },

  [sq('builder', 3)]: {
    id: sq('builder', 3),
    chain: 'builder',
    order: 3,
    name: 'City Architect',
    description: 'Own 50 buildings and build on 75 tiles.',
    objectives: [
      { type: 'buildings_total', count: 50 },
      { type: 'tiles_built', count: 75 },
    ],
    rewards: { gold: 2500, steel: 500, machinery: 200 },
    unlockRequirements: { sideQuest: sq('builder', 2) },
  },

  [sq('builder', 4)]: {
    id: sq('builder', 4),
    chain: 'builder',
    order: 4,
    name: 'Realm Designer',
    description: 'Own 75 buildings and build on 150 tiles.',
    objectives: [
      { type: 'buildings_total', count: 75 },
      { type: 'tiles_built', count: 150 },
    ],
    rewards: { gold: 10000, energy: 3000, science: 2000 },
    unlockRequirements: { sideQuest: sq('builder', 3) },
  },

  [sq('builder', 5)]: {
    id: sq('builder', 5),
    chain: 'builder',
    order: 5,
    name: 'Grand Architect',
    description: 'Own 100 buildings and build on 250 tiles.',
    objectives: [
      { type: 'buildings_total', count: 100 },
      { type: 'tiles_built', count: 250 },
    ],
    rewards: { gold: 50000, magic: 10000, artifacts: 5000 },
    unlockRequirements: { sideQuest: sq('builder', 4) },
    isChainFinale: true,
  },
};

export default sideQuests;
